import { useState, FormEvent, useEffect } from 'react';
import { Link, useLocation } from 'wouter';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AuthCard, VerificationCodeInput } from '@/components/auth';
import { useAuthFlow } from '@/hooks/useAuthFlow';
import { Loader2, AlertCircle, CheckCircle, Mail } from 'lucide-react';
import { toast } from 'sonner';

export default function VerifyEmail() {
  const [, setLocation] = useLocation();
  const { confirmSignup, resendCode, isSubmitting, error, clearError, pendingEmail } = useAuthFlow();

  // Get email from pending email (wouter doesn't support state in navigation)
  const email = pendingEmail || '';

  const [code, setCode] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);
  const [verificationSuccess, setVerificationSuccess] = useState(false);

  // Redirect if no email
  useEffect(() => {
    if (!email) {
      setLocation('/signup');
    }
  }, [email, setLocation]);

  // Cooldown timer for resend
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(prev => prev - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    clearError();

    if (code.length !== 6) {
      return;
    }

    const result = await confirmSignup(email, code);

    if (result.success) {
      setVerificationSuccess(true);
      toast.success('Email verified successfully!');

      // Short delay to show success, then redirect to onboarding
      setTimeout(() => {
        setLocation('/onboarding/profile');
      }, 1500);
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0) return;

    clearError();
    const result = await resendCode(email);

    if (result.success) {
      toast.success('Verification code sent!');
      setResendCooldown(60); // 60 second cooldown
      setCode(''); // Clear existing code
    }
  };

  // Auto-submit when 6 digits entered
  useEffect(() => {
    if (code.length === 6 && !isSubmitting && !verificationSuccess) {
      handleSubmit({ preventDefault: () => {} } as FormEvent);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  if (verificationSuccess) {
    return (
      <AuthCard
        title="Email Verified!"
        description="Your account has been verified successfully"
      >
        <div className="flex flex-col items-center py-6">
          <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mb-4">
            <CheckCircle className="h-8 w-8 text-green-600" />
          </div>
          <p className="text-muted-foreground text-center">
            Redirecting to setup your profile...
          </p>
          <Loader2 className="h-5 w-5 animate-spin mt-4 text-amber-500" />
        </div>
      </AuthCard>
    );
  }

  return (
    <AuthCard
      title="Verify your email"
      description={`We sent a verification code to ${email}`}
      footer={
        <p>
          Wrong email?{' '}
          <Link href="/signup" className="text-amber-600 hover:text-amber-700 font-medium">
            Go back
          </Link>
        </p>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Email indicator */}
        <div className="flex items-center justify-center gap-2 p-3 bg-amber-50 dark:bg-amber-950/30 rounded-lg">
          <Mail className="h-5 w-5 text-amber-600" />
          <span className="text-sm font-medium">{email}</span>
        </div>

        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error.message}</AlertDescription>
          </Alert>
        )}

        <VerificationCodeInput
          value={code}
          onChange={setCode}
          disabled={isSubmitting}
          label="Enter 6-digit code"
        />

        <Button
          type="submit"
          className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"
          disabled={isSubmitting || code.length !== 6}
        >
          {isSubmitting ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Verifying...
            </>
          ) : (
            'Verify Email'
          )}
        </Button>

        <div className="text-center">
          <p className="text-sm text-muted-foreground mb-2">
            Didn&apos;t receive the code?
          </p>
          <Button
            type="button"
            variant="ghost"
            onClick={handleResend}
            disabled={resendCooldown > 0 || isSubmitting}
            className="text-amber-600 hover:text-amber-700"
          >
            {resendCooldown > 0 ? (
              `Resend code in ${resendCooldown}s`
            ) : (
              'Resend verification code'
            )}
          </Button>
        </div>

        <p className="text-xs text-center text-muted-foreground">
          Check your spam folder if you don&apos;t see the email.
          The code expires in 24 hours.
        </p>
      </form>
    </AuthCard>
  );
}
